import { Injectable, inject } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Asset, InstrumentType, OrderDirection, Position, Quote } from '@zero/shared/util-models';
import { OrderFormGroup, TradeService, TradingTimesService } from '@zero/trade/domain';
import { minimumOrderValue } from 'constants/shared-constants';

type PositionDataFunc = () => Position;

@Injectable({ providedIn: 'root' })
export class TradeCreateValidators {
  readonly #tradeService = inject(TradeService);
  readonly #tradingTimesService = inject(TradingTimesService);

  public validateExperienceAndFtg(): ValidatorFn {
    return (form: FormGroup<OrderFormGroup>): ValidationErrors | null => {
      if (!this.#tradeService.asset.value) {
        return null;
      }

      const { experienceAndFtgOK } = this.#tradeService.asset.value;
      if (form.get('direction').value === 'buy' && experienceAndFtgOK !== 'OK') {
        return { experienceAndFtgNotOkay: true };
      }
      return null;
    };
  }

  public validateActiveTradingHours(): ValidatorFn {
    return (form: FormGroup<OrderFormGroup>): ValidationErrors | null => {
      if (!this.#tradeService.asset.value) {
        return null;
      }

      const isBuy = form.get('direction').value === 'buy';
      const isMarket = form.get('execType').value === 'market';
      const isActiveTradingHours = this.#tradingTimesService.isActiveTradingHours(new Date(), this.#tradeService.asset.value);
      return isBuy && isMarket && !isActiveTradingHours
        ? {
            noActiveTradingHours: true,
          }
        : null;
    };
  }

  public validateDerivativeState(): ValidatorFn {
    return (form: FormGroup<OrderFormGroup>): ValidationErrors | null => {
      if (!this.#tradeService.asset.value || !this.#tradeService.quote.value) {
        return null;
      }

      const { instrumentType, knockedOutFlag, endfaellig } = this.#tradeService.asset.value;
      if (instrumentType !== InstrumentType.Derivative) {
        return null;
      }

      const { ask, bid } = this.#tradeService.quote.value;
      const isKnockedOut: boolean = knockedOutFlag;
      const isSoldOut: boolean = !isKnockedOut && ask === 0 && bid > 0;
      const isEndfaellig: boolean = endfaellig;

      if (form.get('direction').value === 'buy' && isSoldOut) {
        return {
          derivativeSoldOut: true,
        };
      }

      if (isKnockedOut) {
        return {
          derivativeKnockedOut: true,
        };
      }

      if (isEndfaellig) {
        return {
          derivativeExpired: true,
        };
      }

      return null;
    };
  }

  public validateMinimumOrderValue(positionData: PositionDataFunc): ValidatorFn {
    return (form: FormGroup<OrderFormGroup>): ValidationErrors | null => {
      if (!(form.get('direction').value && form.get('execType').value && form.get('quantityForm')?.get('quantity')?.value)) {
        return null;
      }

      const { value: quote } = this.#tradeService.quote;
      const { value: asset } = this.#tradeService.asset;
      const control = form.get('quantityForm').get('quantity');
      let minimumQuantity: number;
      try {
        minimumQuantity = this.#getMinimumQuantity(control, asset, quote, positionData());
      } catch {
        return null;
      }

      const { value: quantity } = control;

      let returnValue = null;
      if (minimumQuantity > Number(quantity) && Number(quantity) >= 1) {
        const minimumOrderValueForAsset = asset?.minimumOrderValue;
        returnValue = {
          orderValueTooLow: { key: 'trade.create.quantity.errors.order-value-too-low', minimumQuantity, minimumOrderValueForAsset },
        };
      }

      if (returnValue) {
        control.setErrors(returnValue);
      } else if (control.hasError('orderValueTooLow')) {
        control.updateValueAndValidity({ onlySelf: true });
      }

      return returnValue;
    };
  }

  public validateQuantity(positionData: PositionDataFunc): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value && control.value !== 0) {
        return null;
      }

      const { asset } = this.#tradeService;
      const { instrumentType, minQty, qtyScale, fractionalTrading } = asset.value;
      const rootForm = control.parent.parent as FormGroup<OrderFormGroup>;
      const direction = rootForm.get('direction').value;
      const isSell = direction === 'sell';

      const handleNonFractionalTrading = () => {
        if (isSell) {
          if (Validators.pattern('[0-9]*')(control) && positionData()?.availableQuantity % 1 === 0) {
            errors = { ...errors, fragments: 'trade.create.quantity.errors.fragments' };
          }
        } else if (Validators.min(1)(control)) {
          errors = {
            ...errors,
            quantityInvalid: {
              value: 'trade.create.quantity.errors.minimum-default-value',
              quantity: 'trade.create.quantity.errors.minimum-default-quantity',
            },
          };
        } else if (Validators.pattern('[0-9]*')(control)) {
          errors = { ...errors, fragments: 'trade.create.quantity.errors.fragments' };
        }
      };

      const hasMoreThanMaximumFractionDigits = (num: number, digits: number): boolean => {
        const numStr = num.toString();
        const decimalIndex = numStr.indexOf('.');
        if (decimalIndex === -1) {
          return false;
        }

        return numStr.slice(decimalIndex + 1).length > digits;
      };

      const handleFractionalTrading = (quantity: number): void => {
        if (hasMoreThanMaximumFractionDigits(quantity, 3)) {
          errors = { ...errors, maximumFraction: { key: 'trade.create.quantity.errors.maximum-fraction', digits: 3 } };
        }
      };

      const validateQuantityValue = () => {
        //special handling fractional trading + fraction sellable
        const isFractionAllowed =
          fractionalTrading || (isSell && Validators.pattern('[0-9]*')(control) && positionData()?.availableQuantity % 1 !== 0);

        let calcMinQty = 1;
        if (isFractionAllowed) {
          calcMinQty = 0.001;
        }

        const qty = Number(control.value);
        if (qty === 0) {
          errors = { ...errors, zero: { minimumQuantity: calcMinQty } };
          return;
        }

        // dont allow stop or limit values orders with fractional trading
        if (
          (rootForm.get('optionsForm')?.get('stopPrice').value || rootForm.get('optionsForm')?.get('limitPrice').value) &&
          qty % 1 !== 0
        ) {
          errors = { ...errors, stopLimitFraction: true };
          return;
        }

        const { value: quote } = this.#tradeService.quote;
        const quote2use = this.#getQuoteToCalcMinimumQuantity(rootForm, quote, direction);

        if (quote2use && +(qty * quote2use).toFixed(2) < 0.01) {
          errors = { ...errors, minOrderValue: { minimumQuantity: calcMinQty } };
          return;
        }

        !isFractionAllowed && handleNonFractionalTrading();
        isFractionAllowed && handleFractionalTrading(qty);
      };

      let errors = null;
      if (isSell && positionData()?.availableQuantity < control.value) {
        return { ...errors, notEnoughQuantity: true };
      }

      switch (instrumentType) {
        case InstrumentType.Crypto:
          if (Number(control.value) < minQty) {
            errors = { ...errors, minQty: { key: 'trade.create.quantity.errors.minimum-quantity', minQty, qtyScale } };
          }
          if (hasMoreThanMaximumFractionDigits(Number(control.value), qtyScale)) {
            errors = { ...errors, maximumFraction: { key: 'trade.create.quantity.errors.maximum-fraction', digits: qtyScale } };
          }
          break;
        case InstrumentType.Share:
        case InstrumentType.Etf:
        case InstrumentType.Funds:
        default:
          validateQuantityValue();
          break;
      }

      return errors;
    };
  }

  public validateStopLimitPrice(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const stop = control.get('stopPrice');
      const stoplimit = control.get('stopLimitPrice');
      const direction: OrderDirection = control.parent.get('direction').value;
      if (!stop.value || !stoplimit.value) {
        return null;
      }

      let error = null;
      switch (direction) {
        case 'buy':
          error = stop.value >= stoplimit.value ? { stopLimit: 'trade.create.options.stop.buy.error' } : null;
          break;
        case 'sell':
          error = stop.value <= stoplimit.value ? { stopLimit: 'trade.create.options.stop.sell.error' } : null;
          break;
        default:
          break;
      }

      return error;
    };
  }

  public validateOco(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const stopLoss = control.get('stopLoss');
      const takeProfit = control.get('takeProfit');
      if (!stopLoss.value || !takeProfit.value) {
        return null;
      }

      if (takeProfit.value <= stopLoss.value) {
        return { oco: 'trade.create.options.oco.error.less' };
      }

      return null;
    };
  }

  public validateTsl(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const trailingAbs = control.get('trailingAbs');
      const trailingRel = control.get('trailingRel');
      const type = control.get('trailingType');
      if (!type?.value || (!trailingAbs?.value && !trailingRel?.value)) {
        return { initial: true };
      }

      switch (type.value) {
        case 'abs':
          if (trailingRel.value > 100) {
            return { tsl: 'trade.create.options.tsl.error.abs-exceed' };
          } else if (trailingAbs.value <= 0) {
            return { tsl: 'trade.create.options.tsl.error.abs' };
          } else {
            return null;
          }
        case 'rel':
          return trailingRel.value < 0 || trailingRel.value > 100 ? { tsl: 'trade.create.options.tsl.error.rel' } : null;
        default:
          return null;
      }
    };
  }

  #getMinimumQuantity = (control: AbstractControl, asset: Asset, quote: Quote, position: Position): number => {
    if (!asset) {
      throw new Error('no asset defined');
    }

    const rootForm = control.parent.parent as FormGroup<OrderFormGroup>;
    const direction = rootForm.get('direction').value;
    const isSell = direction === 'sell';
    const quote2use = this.#getQuoteToCalcMinimumQuantity(rootForm, quote, direction);

    const minimumOrderValueForAsset = asset?.minimumOrderValue ? asset.minimumOrderValue : minimumOrderValue;

    let minimumQuantity = 0;
    switch (asset.instrumentType) {
      case InstrumentType.Derivative:
        if (quote2use) {
          minimumQuantity = Math.ceil(minimumOrderValueForAsset / quote2use);
        }
        if (minimumQuantity > 0 && isSell && position) {
          minimumQuantity = Math.min(minimumQuantity, position.availableQuantity);
          minimumQuantity = Math.floor(minimumQuantity);
        }

        if (minimumQuantity <= 0 && quote) {
          minimumQuantity = 1; // ein Stück muss mindestens gehandelt werden
        }
        break;
      default:
        break;
    }

    return minimumQuantity;
  };

  #getQuoteToCalcMinimumQuantity = (form: FormGroup<OrderFormGroup>, quote: Quote, direction: OrderDirection): number => {
    const limitPrice = form.get('optionsForm')?.get('limitPrice');
    const stopLimitPrice = form.get('optionsForm')?.get('stopLimitPrice');
    const stopPrice = form.get('optionsForm')?.get('stopPrice');
    const execType = form.get('execType')?.value;
    const isBuy = direction === 'buy';

    switch (execType) {
      case 'market':
        if (!quote) {
          return 0;
        }
        return isBuy ? quote.ask : quote.bid;
      case 'limit':
        if (limitPrice.value) {
          return limitPrice.value;
        } else if (isBuy && quote?.ask) {
          return Math.min(0, quote.ask);
        }
        break;
      case 'stop':
        if (stopPrice.value) {
          return stopPrice.value;
        } else if (stopLimitPrice.value && !isBuy) {
          return stopLimitPrice.value;
        }
        break;
      default:
        break;
    }
    return 0;
  };
}
